/**
 * 견적서로 원가분석표 만들기
 *
 * ① 기존 견적서 (v2.236.0 · 2026-10-09) — 「이 견적서의 원가 보기」
 *   운영 자료(10-09): 견적서 115건이 이미 있다 — 표를 새로 짜지 않아도 지난 견적의 마진을 바로 볼 수 있게.
 * ② 작성 중인 견적서 (v2.248.0 · 2026-10-13) — 견적서 작성 창의 [🧮 원가분석] (사용자 「견적서 작성하다가도 원가분석」)
 *
 * 줄은 견적서 품목 그대로(판매 단가 고정 · lib/cost-sheet-calc linesFromQuoteItems), 원가는 **품목 마스터 이름이 같은 줄만**
 *   매입 내역의 최근/평균 매입가로 채운다(이름이 「HDD」처럼 뭉뚱그려진 줄을 엉뚱한 매입에 잇지 않으려고 — 상세내용 부분일치는 쓰지 않는다).
 *   💡 가격 참고로 이미 원가를 아는 줄(작성 중인 견적서)은 그 원가 그대로 · 나머지 줄은 원가 0(직접) — 표에서 [+ 매입 목록에서] 로 채운다.
 */
import { createCostSheet, type CostSheet } from './cost-sheet-api';
import { linesFromQuoteItems, type CostLine } from './cost-sheet-calc';
import type { QuoteDetail, QuoteItemInput } from './quote-api';
import { fetchRecentSupplier, listItems } from './stock-api';

export interface FromQuoteResult {
  sheet: CostSheet;
  /** 매입 원가를 찾아 넣은 줄 수 */
  matched: number;
  total: number;
}

/** 품목 마스터 이름이 같은 줄에 최근/평균 매입가를 채운다 — 이미 원가를 아는 줄(unitCost > 0)은 건드리지 않는다 */
async function fillPurchaseCosts(base: CostLine[]): Promise<{ lines: CostLine[]; matched: number }> {
  let matched = 0;
  if (base.every((l) => l.unitCost > 0)) return { lines: base, matched };
  try {
    const items = await listItems();
    const byName = new Map(items.map((i) => [i.itemName.trim().toLowerCase(), i] as const));
    const lines = await Promise.all(
      base.map(async (l): Promise<CostLine> => {
        if (l.unitCost > 0) return l;
        const hit = byName.get(l.name.trim().toLowerCase());
        if (!hit) return l;
        const info = await fetchRecentSupplier(hit.itemCode).catch(() => null);
        if (!info || !info.count) return { ...l, source: 'purchase', itemCode: hit.itemCode, vendor: hit.vendor ?? '' };
        // 「최근 매입가」가 0(단가 없이 들어온 줄)이면 평균으로 — 가져오기 창과 같은 규칙
        const avg = info.avgPrice || 0;
        const recent = info.recentPrice || avg;
        matched += 1;
        return {
          ...l,
          source: 'purchase',
          itemCode: hit.itemCode,
          vendor: info.supplier || hit.vendor || '',
          recent: recent || null,
          avg: avg || null,
          basis: recent > 0 ? 'recent' : avg > 0 ? 'avg' : 'manual',
          unitCost: recent || avg || 0,
        };
      }),
    );
    return { lines, matched };
  } catch {
    // 품목 마스터를 못 읽으면 원가 없이 만든다 — 표에서 채우면 된다
    return { lines: base, matched: 0 };
  }
}

export async function createCostSheetFromQuote(quote: QuoteDetail): Promise<FromQuoteResult> {
  const { lines, matched } = await fillPurchaseCosts(linesFromQuoteItems(quote.items));
  const sheet = await createCostSheet({
    title: quote.projectName.trim() || quote.quoteNo,
    customer: quote.recipientCompany,
    memo: `견적서 ${quote.quoteNo} 로 만듦 — 판매 단가는 견적서 값으로 고정(↺ 로 마진율 자동)`,
    marginPct: 15,
    roundUnit: 1000,
    lines,
    quoteId: quote.id,
    salesId: quote.salesId ?? null,
  });
  return { sheet, matched, total: lines.length };
}

/**
 * 작성 중인 견적서(저장 전일 수도 있다) → 원가분석표 (v2.248.0)
 *   판매 단가: 적어 둔 단가는 고정(sellUnit · ↺ 로 마진율 자동) · **0 원인 줄은 처음부터 마진율 자동** — 아직 안 정한 단가다.
 *   견적서 번호는 수정 중인 견적서면 바로 · 새 견적서면 저장할 때 서버가 잇는다(견적서 저장 본문의 costSheetId).
 */
export async function createCostSheetFromDraft(input: {
  title: string;
  customer: string;
  items: readonly QuoteItemInput[];
  quoteId: number | null;
  quoteNo: string | null;
  salesId: string | null;
}): Promise<FromQuoteResult> {
  const base = linesFromQuoteItems(input.items).map((l) => (l.sellUnit !== null && l.sellUnit > 0 ? l : { ...l, sellUnit: null }));
  const { lines, matched } = await fillPurchaseCosts(base);
  const sheet = await createCostSheet({
    title: input.title.trim() || '견적서 작성 중',
    customer: input.customer.trim(),
    memo: input.quoteNo
      ? `견적서 ${input.quoteNo} 작성 창에서 만듦 — 적어 둔 판매 단가는 고정(↺ 로 마진율 자동)`
      : '견적서 작성 창에서 만듦 — 견적서를 저장하면 그 번호가 여기에 이어집니다',
    marginPct: 15,
    roundUnit: 1000,
    lines,
    quoteId: input.quoteId,
    salesId: input.salesId,
  });
  return { sheet, matched, total: lines.length };
}
